import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useConfirmDelete } from '@/components/ConfirmDialogs';
import { DateField } from '@/components/ui/date-field';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * PROMPT 5 — extraído de PacienteDetailPage. Tab de pagamentos por paciente (admin).
 * Comportamento e data-testids preservados verbatim.
 */
export default function PagamentosPatientTab({ patientId }) {
  const confirmDelete = useConfirmDelete();
  const [data, setData] = useState({ payments: [], pending_consultations: [], total_paid: 0, pending_count: 0 });
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [invDate, setInvDate] = useState(new Date().toISOString().split('T')[0]);
  const [uploading, setUploading] = useState(false);
  const [invConsult, setInvConsult] = useState('');
  const [consults, setConsults] = useState([]);
  useEffect(() => { axios.get(`${API}/api/patients/${patientId}/consultations`).then(r => setConsults(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, [patientId]);
  const fetch = useCallback(async () => {
    try { const { data: r } = await axios.get(`${API}/api/patients/${patientId}/payments`); setData(r); } catch {}
    finally { setLoading(false); }
  }, [patientId]);
  const fetchInvoices = useCallback(async () => {
    try { const { data: r } = await axios.get(`${API}/api/patients/${patientId}/invoices`); setInvoices(Array.isArray(r) ? r : []); } catch {}
  }, [patientId]);
  useEffect(() => { fetch(); fetchInvoices(); }, [fetch, fetchInvoices]);

  const uploadInvoice = async (fileObj, replaceId = '') => {
    if (!fileObj) return;
    if (fileObj.type !== 'application/pdf' && !fileObj.name.toLowerCase().endsWith('.pdf')) { toast.error('Apenas PDF'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', fileObj);
      fd.append('invoice_date', invDate);
      if (invConsult) fd.append('consultation_id', invConsult);
      if (replaceId) fd.append('replace_id', replaceId);
      await axios.post(`${API}/api/patients/${patientId}/invoices/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(replaceId ? 'Fatura substituída' : 'Fatura carregada');
      fetchInvoices();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao carregar'); } finally { setUploading(false); }
  };
  const deleteInvoice = async (id) => {
    const inv = invoices.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar a fatura "${inv?.filename || ''}" de ${inv?.invoice_date || ''}.` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/patients/${patientId}/invoices/${id}`); toast.success('Fatura eliminada'); fetchInvoices(); } catch { toast.error('Erro'); }
  };

  const markPaid = async (cid) => {
    try { await axios.put(`${API}/api/consultations/${cid}`, { is_paid: true }); toast.success('Marcado como pago'); fetch(); }
    catch { toast.error('Erro'); }
  };

  if (loading) return <div className="text-center py-8 text-brand-muted text-sm" data-testid="pagamentos-loading">A carregar...</div>;

  return (
    <div data-testid="tab-pagamentos" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-lg text-brand-text">Pagamentos</h3>
          <p className="text-[11px] text-brand-muted">{data.payments.length} pagamento(s) registado(s) · Total {data.total_paid.toFixed(2)}€</p>
        </div>
        {data.pending_count > 0 && (
          <span className="text-[10px] bg-red-50 text-red-600 px-2 py-1 rounded-full font-medium" data-testid="pagamentos-pending-badge">
            {data.pending_count} por pagar
          </span>
        )}
      </div>

      {data.pending_consultations.length > 0 && (
        <div className="bg-amber-50/40 border border-amber-200 rounded-2xl p-3" data-testid="pagamentos-pending-list">
          <p className="text-xs font-semibold text-amber-700 mb-2">Consultas por pagar</p>
          <ul className="space-y-1.5">
            {data.pending_consultations.map(c => (
              <li key={c.id} className="flex items-center justify-between bg-white rounded-xl px-3 py-2" data-testid={`pending-consult-${c.id}`}>
                <span className="text-xs text-brand-text">{fmtDatePt(c.date)} · {c.gross_amount}€</span>
                <button onClick={() => markPaid(c.id)} className="text-[10px] bg-brand-green text-white rounded-full px-3 py-1" data-testid={`mark-paid-${c.id}`}>Marcar pago</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.payments.length === 0 ? (
        <div className="bg-white border border-brand-border rounded-2xl p-6 text-center text-xs text-brand-muted" data-testid="pagamentos-empty">
          Sem pagamentos registados.
        </div>
      ) : (
        <ul className="space-y-1.5">
          {data.payments.map(p => (
            <li key={p.id} className="bg-white border border-brand-border rounded-2xl p-3 flex items-center justify-between" data-testid={`payment-row-${p.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-brand-text"><strong>{p.amount}€</strong> · {p.plan}</p>
                <p className="text-[10px] text-brand-muted">{fmtDatePt(p.date)} · {p.method}{p.notes ? ` · ${p.notes}` : ''}</p>
              </div>
              {p.stripe_payment_intent_id && <span className="text-[9px] bg-violet-50 text-violet-700 px-2 py-0.5 rounded-full">Stripe</span>}
            </li>
          ))}
        </ul>
      )}

      {/* Faturas (PDF) — upload pela nutri, visível à paciente em "As minhas faturas" */}
      <div className="bg-white border border-brand-border rounded-2xl p-4" data-testid="invoices-section">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-heading text-sm text-brand-text">Faturas (PDF)</h3>
          <div className="flex items-center gap-2">
            <DateField value={invDate} onChange={e => setInvDate(e.target.value)} className="border border-brand-border rounded-xl px-2 py-1 text-xs" data-testid="invoice-date-input" />
            <select value={invConsult} onChange={e => setInvConsult(e.target.value)} className="border border-brand-border rounded-xl px-2 py-1 text-xs max-w-[190px]" data-testid="invoice-consultation-select">
              <option value="">Consulta: automática</option>
              {consults.map(c => <option key={c.id} value={c.id}>{fmtDatePt(c.date)}{c.is_first ? ' · 1.ª' : ''}</option>)}
            </select>
            <label className={`text-[11px] bg-brand-green text-white rounded-full px-3 py-1.5 font-medium cursor-pointer ${uploading ? 'opacity-50 pointer-events-none' : ''}`} data-testid="invoice-upload-btn">
              {uploading ? 'A carregar...' : '+ Carregar fatura'}
              <input type="file" accept="application/pdf" className="hidden" onChange={e => { uploadInvoice(e.target.files[0]); e.target.value = ''; }} data-testid="invoice-file-input" />
            </label>
          </div>
        </div>
        {invoices.length === 0 ? (
          <p className="text-xs text-brand-muted text-center py-3" data-testid="invoices-empty">Sem faturas carregadas.</p>
        ) : (
          <ul className="space-y-1.5">
            {invoices.map(inv => (
              <li key={inv.id} className="flex items-center justify-between bg-brand-bg/40 rounded-xl px-3 py-2" data-testid={`invoice-row-${inv.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-brand-text truncate">{inv.filename}</p>
                  <p className="text-[10px] text-brand-muted">{inv.invoice_date}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <a href={`${API}/api/invoices/${inv.id}/pdf`} target="_blank" rel="noreferrer" className="text-[10px] text-brand-green hover:underline" data-testid={`invoice-view-${inv.id}`}>Ver</a>
                  <label className="text-[10px] text-brand-muted hover:text-brand-text cursor-pointer" data-testid={`invoice-replace-${inv.id}`}>
                    Substituir
                    <input type="file" accept="application/pdf" className="hidden" onChange={e => { uploadInvoice(e.target.files[0], inv.id); e.target.value = ''; }} />
                  </label>
                  <button onClick={() => deleteInvoice(inv.id)} className="text-[10px] text-red-500 hover:underline" data-testid={`invoice-delete-${inv.id}`}>Eliminar</button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
